import { CloudDownloadOutlined, SendOutlined } from '@ant-design/icons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { Modal, notification, Popconfirm, Radio, Spin } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type DeliveryTicketModalProps = {
  isVisible: boolean
  onCancel: () => void
  onOk?: () => void
  data: { ticketCache: Record<string, string>; selectedOrder: string; drops: Gravitate.StoreDrop[] }
  ticketCache: Record<string, string>
  setTicketCache: (cache: Record<string, string>) => void
}

export function DeliveryTicketModal({
  isVisible,
  onCancel,
  onOk,
  data,
  ticketCache,
  setTicketCache,
}: DeliveryTicketModalProps) {
  const stores = [...new Set(data?.drops?.map((d) => d.store))]
  const [ticketIndex, setTicketIndex] = useState(0)
  const downloadLink = useRef()
  const [file, setFile] = useState()
  const [isSending, setSending] = useState(false)
  const [isTicketLoading, setTicketLoading] = useState(false)

  const selectedTicket = useMemo(() => stores[ticketIndex], [stores, ticketIndex])

  const downloadTicket = (url) => {
    downloadLink.current.href = url
    downloadLink.current.setAttribute('download', 'ticket.pdf')
    downloadLink.current.click()
  }

  useEffect(() => {
    if (!isVisible || !data || !stores?.length) return
    const order_number = parseInt(data?.selectedOrder)
    const hit = ticketCache[`${data.selectedOrder}::${selectedTicket}`]
    if (hit) {
      setFile(hit)
    } else {
      setTicketLoading(true)
      api
        .getDeliveryTicketPDF(order_number, selectedTicket)
        .then((resp) => {
          setTicketCache({
            ...ticketCache,
            [`${data.selectedOrder}::${selectedTicket}`]: URL.createObjectURL(
              new Blob([resp?.data], { type: 'application/pdf' })
            ),
          })

          setFile(window.URL.createObjectURL(new Blob([resp?.data], { type: 'application/pdf' })))
        })
        .catch(handleCancel)
        .finally(() => setTicketLoading(false))
    }
  }, [isVisible, selectedTicket])

  const handleDownload = () => {
    const hit = ticketCache[`${data.selectedOrder}::${selectedTicket}`]
    if (hit) {
      downloadTicket(hit)
    }
  }

  const handleSend = () => {
    const hit = ticketCache[`${data.selectedOrder}::${selectedTicket}`]
    if (hit) {
      setSending(true)
      const order_number = parseInt(data?.selectedOrder)
      api
        .sendDeliveryTicket(order_number, selectedTicket)
        .then((_) => {
          notification.success({ message: 'Delivery ticket successfully emailed!' })
        })
        .finally(() => setSending(false))
    }
  }

  const handleCancel = useCallback(() => {
    setTicketIndex(0)
    onCancel()
  }, [])

  const containerStyle = {
    height: 'calc(100vh - 400px)',
  }
  const { scopes } = useSecurityContext()
  const canEdit = scopes.order_movements?.edit

  return (
    <>
      <a ref={downloadLink} />

      <Modal
        title={`Delivery Tickets - Order #${data?.selectedOrder}`}
        visible={isVisible}
        width='60vw'
        style={{ width: '60vw' }}
        bodyStyle={{ overflowY: 'scroll' }}
        onCancel={handleCancel}
        onOk={onOk}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <div style={{ flex: 1 / 3 }} />
            <div style={{ display: 'flex', flexGrow: 1, justifyContent: 'center' }}>
              {stores?.length > 1 && (
                <Radio.Group
                  options={stores?.map((store, index) => ({ label: `Site ${store}`, value: index }))}
                  onChange={({ target: { value } }) => setTicketIndex(value)}
                  value={ticketIndex}
                  optionType='button'
                />
              )}
            </div>
            <Horizontal gap={8} justifyContent='flex-end'>
              <GraviButton
                icon={<CloudDownloadOutlined />}
                disabled={isTicketLoading}
                onClick={handleDownload}
                buttonText='Download'
                theme2
              />
              {canEdit && (
                <Popconfirm title='Are you sure you want to email this delivery ticket?' onConfirm={handleSend}>
                  <GraviButton
                    icon={<SendOutlined />}
                    disabled={isTicketLoading}
                    loading={isSending}
                    buttonText='Send'
                    theme2
                  />
                </Popconfirm>
              )}
            </Horizontal>
          </div>
        }
      >
        {isTicketLoading ? (
          <div
            style={{
              ...containerStyle,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Spin size='large' />
              <Texto weight={600} appearance='medium' category='heading-small'>
                Generating Ticket
              </Texto>
            </div>
          </div>
        ) : (
          <div style={{ ...containerStyle }}>
            <embed style={{ width: '100%', height: '100%' }} src={file} />
          </div>
        )}
      </Modal>
    </>
  )
}
